import React, { Fragment, useState, useEffect, ReactNode, ReactElement } from 'react'
import localforage from 'myStore'

import { Navigate, Location as RouterLocation } from 'react-router'
import { withRouter } from 'router'
import { connect } from 'react-redux'
import NProgress from 'nprogress'
// import Loader from 'components/cleanui/layout/Loader'
import PublicLayout from './Public'
import AuthLayout from './Auth'
import MainLayout from './Main'
// import MainLayout2 from './Main2'
import EmptyLayout from './Empty'
import { getSearchParams, makeSearchString } from 'utils'
import { isOfficeChatWebview } from 'libs/userAgent'
import { isDevelop } from 'vars'

interface LayoutType {
  [key: string]: React.ComponentType<any>
}

const Layouts: LayoutType = {
  public: PublicLayout,
  auth: AuthLayout,
  main: MainLayout,
  empty: EmptyLayout,
  outlook: EmptyLayout,
}

export const bypassPaths = [
  '/auth/entry',
  '/auth/callback',
  '/auth/redirect',
  '/auth/access-denied',
  '/auth/restart-required',
]

// const Main2List = ['/main', '/email']

interface UserState {
  authorized: boolean
  loading: boolean
  serverType?: string
}

interface MapStateToPropsReturn {
  user: UserState
}

const mapStateToProps = ({ user }: { user: UserState }): MapStateToPropsReturn => ({ user })
// let previousPath = ''
let previousPathname = ''

interface LayoutProps {
  user: UserState
  children: ReactNode
  location: RouterLocation
}

const Layout: React.FC<LayoutProps> = ({ user, children, location }) => {
  // const Layout = ({ children, location }) => {
  // NProgress & ScrollTop Management
  // const { pathname, search } = location;

  const [curLayout, setCurLayout] = useState<ReactElement | null>(null)

  useEffect(() => {
    const processFunc = async () => {
      const { pathname, search } = location

      // const currentPath = pathname + search
      if (previousPathname !== pathname) {
        window.scrollTo(0, 0)
        NProgress.start()
      }
      setTimeout(() => {
        NProgress.done()
        // previousPath = currentPath;
        previousPathname = pathname
      }, 300)

      // Layout Rendering
      const getLayoutInfo = (): { layout: string; needAuth: boolean } => {
        if (pathname === '/') {
          return {
            layout: 'public',
            needAuth: false,
          }
        }
        // if (pathname.indexOf('/downloadFile') === 0) {
        //   return 'empty'
        // }
        if (/^\/auth(?=\/|$)/i.test(pathname)) {
          return {
            layout: 'auth',
            needAuth: false,
          }
        }

        // dev 전용 테스트 페이지(/dev/*)는 로그인 없이 접근 가능해야 한다. dev 빌드에서만 동작한다.
        if (isDevelop && /^\/dev(?=\/|$)/i.test(pathname)) {
          return {
            layout: 'empty',
            needAuth: false,
          }
        }

        if (/^\/plain\//i.test(pathname)) {
          return {
            layout: 'empty',
            needAuth: true,
          }
        }

        // if (pathname.indexOf('/outlook') === 0) {
        //   return 'outlook'
        // }
        return {
          layout: 'main',
          needAuth: true,
        }
      }

      const { layout, needAuth } = getLayoutInfo()
      const Container = Layouts[layout]

      const token = await localforage.getItem('app.user.token')
      const isUserAuthorized = user.authorized || token != null
      const isUserLoading = user.loading
      const isAuthLayout = layout === 'auth'
      // const isOutlookLayout = layout === 'outlook'

      const BootstrappedLayout = (): ReactElement | null => {
        // show loader when user in check authorization process, not authorized yet and not on login pages
        const curPath = window.location.pathname
        const isBypass = bypassPaths.indexOf(curPath) > -1
        if (isBypass) {
          return <Container>{children}</Container>
        }

        if (isUserLoading && !isAuthLayout) {
          return null
        }

        // redirect to login page if current is not login page and user not authorized
        if (needAuth && !isAuthLayout && !isUserAuthorized) {
          // OfficeChat webview에서는 재로그인 흐름이 없으므로 재실행 안내 화면으로 보낸다 (OFM-229)
          if (isOfficeChatWebview()) {
            return <Navigate to="/auth/restart-required" />
          }
          let params = ''
          const searchParams = getSearchParams()
          if (searchParams != null && Object.keys(searchParams).length > 0) {
            params = '&params=' + btoa(JSON.stringify(searchParams))
          }
          if (pathname != null && pathname !== '/' && pathname.indexOf('/auth') < 0) {
            return <Navigate to={`/auth/login?path=${pathname}${params}`} />
          }
          return <Navigate to="/auth/login" />
        }

        if (isUserAuthorized && pathname.indexOf('/auth/login') === 0) {
          if (search?.length > 0) {
            const searchParams = getSearchParams()
            const path = searchParams.path
            if (path) {
              let paramStr = ''
              if (
                searchParams != null &&
                searchParams.params != null &&
                typeof searchParams.params === 'string'
              ) {
                const params = JSON.parse(atob(searchParams.params))
                const str = makeSearchString(params)
                paramStr = `?${str}`
              }
              return <Navigate to={`${path}${paramStr}`} />
            }
          }
          return <Navigate to="/main" />
        }

        // in other case render previously set layout
        return <Container>{children}</Container>
      }

      const layoutElem = BootstrappedLayout()
      setCurLayout(layoutElem)
    }
    processFunc()
  }, [user, children, location])

  return (
    // 문서 title 은 항상 "OfficeMail" 상수이며 index.html 의 정적 <title> 이 첫 페인트부터 제공한다.
    // 여기서 React 19 native <title> 을 또 렌더하면 head 에 <title> 이 2개로 중복되므로 렌더하지 않는다.
    // (기존 react-helmet 은 단일 title 을 갱신했으나 native 는 append 되어 중복 발생)
    <Fragment>{curLayout}</Fragment>
  )
}

export default withRouter(connect(mapStateToProps)(Layout))
